(() => {
  "use strict";
  const boot = (root) => {
  const IMG = { closed: root.dataset.imgClosed, open: root.dataset.imgOpen, top: root.dataset.imgTop };

  /* ── Opgemeten geometrie per foto (bronpixels, 1536² origineel) ──────────
     cx,cy  : midden van de rand-ellips (breedste punt van de basis)
     a      : halve breedte van de rand
     bF,bB  : halve hoogte van de ellips aan voor- en achterkant (perspectief)
     sk     : zichtbare onderkant (kom) onder de voorrand
     yh     : scharnierlijn (achterkant van de rand)
     H,D    : zichtbare hoogte van de open deksel / diameter                  */
  const P = {
    closed: {cx:770,   cy:766, a:275.5, bF:142.4, bB:124.9, sk:82.6, yh:641.1, dome:28},
    open:   {cx:765.5, cy:955, a:325.5, bF:176.7, bB:155.0, sk:87.3, yh:800,  H:480, D:651},
    top:    {cx:769.5, cy:872, a:293.5, bF:254.0, bB:257.0, sk:3.0,  yh:615,  H:156, D:587}
  };
  for (const k in P){
    const p = P[k];
    p.e   = Math.asin((p.bF + p.bB) / (2 * p.a));      // camerahoogte (rad)
    p.eps = (p.bF - p.bB) / (p.bF + p.bB);             // perspectief-asymmetrie
    p.skr = p.sk / p.a;
    if (p.H) p.h = p.H / p.D;                           // sin(φ − e) van de foto
  }
  const E0 = P.closed.e, E1 = P.open.e, E2 = P.top.e;
  const PHI_END = E2 + Math.asin(P.top.h);             // dekselhoek eindstand
  const D2R = Math.PI / 180;
  const DEPTH = 0.16;        // verzonken lade (in straal-eenheden)
  const SHADOW_ROOM = 0.34;  // ruimte onder de doos voor de schaduw

  const clamp = (v, a=0, b=1) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const sstep = (a, b, v) => { const t = clamp((v - a) / (b - a)); return t * t * (3 - 2 * t); };
  const easeIO = t => t < .5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3) / 2;
  const easeSine = t => -(Math.cos(Math.PI * t) - 1) / 2;
  // stuksgewijs lineair over de drie camerahoogtes
  const byE = (e, v0, v1, v2) => e <= E1 ? lerp(v0, v1, clamp((e - E0) / (E1 - E0))) : lerp(v1, v2, clamp((e - E1) / (E2 - E1)));

  const grid   = root.querySelector(".er-grid");
  const visual = root.querySelector(".er-visual");
  const stage  = root.querySelector(".er-stage");
  const floatEl= root.querySelector(".er-float");
  const canvas = root.querySelector(".er-canvas");
  const shadow = root.querySelector(".er-shadow");
  const copy   = root.querySelector(".er-copy");
  const caption= root.querySelector(".er-caption");
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ── Animatiestatus ── */
  const S = {
    p: 0, target: 0,              // voortgang 0 = dicht/voor, 1 = open/boven
    tw: null,                     // lopende tween
    phi: 0, vphi: 0,              // deksel (veer)
    peek: 0, peekT: 0, hover: false,
    interacted: false,
    lastE: E0, lastPhi: 0,
    W: 0, H: 0, dpr: 1, capC: .3, capO: .4, fitH: .86,
    desktop: false, dx: 0, dy: 0,
    drag: null, open: false
  };

  /* ── Curves (functie van voortgang p) ── */
  // p loopt lineair in de tijd; elk kanaal heeft zijn eigen curve
  const phiTarget = p => PHI_END * easeSine(clamp(p / 0.46));           // deksel klikt open
  const eAt       = p => lerp(E0, E2, easeIO(clamp((p - 0.1) / 0.9)));   // camera stijgt
  const liftAt    = p => easeIO(clamp((p - 0.12) / 0.78));               // layout schuift
  const zoomAt    = p => easeSine(clamp((p - 0.05) / 0.9));

  /* ── Rig: afgeleide maten voor een gegeven (φ, e, A) ── */
  function rig(phi, e, A){
    const s = Math.sin(e), eps = byE(e, P.closed.eps, P.open.eps, P.top.eps);
    const bF = A * s * (1 + eps), bB = A * s * (1 - eps);
    const sk = Math.max(0.5, A * byE(e, P.closed.skr, P.open.skr, P.top.skr));
    const lid = Math.sin(phi - e);                     // <0: buitenkant zichtbaar
    const sOut = Math.sin(e - phi) / Math.sin(E0);     // schaal buitenkant t.o.v. foto
    const up = Math.max(2 * A * Math.max(lid, 0), P.closed.dome * (A / P.closed.a) * Math.max(sOut, 0));
    return {s, bF, bB, sk, lid, sOut, top: -bB - up, bottom: bF + sk};
  }

  function measure(){
    const r = stage.getBoundingClientRect();
    S.W = stage.clientWidth; S.H = stage.clientHeight;
    S.dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(S.W * S.dpr); canvas.height = Math.round(S.H * S.dpr);
    S.desktop = matchMedia("(min-width: 900px)").matches;
    S.capC = S.desktop ? 0.27 : 0.34;   // max straal gesloten (× breedte)
    S.capO = S.desktop ? 0.40 : 0.37;   // max straal open
    S.fitH = S.desktop ? 0.9 : 0.86;
    if (S.desktop){
      // gesloten: visual gecentreerd in de sectie
      const gw = grid.clientWidth, vl = visual.offsetLeft, vw = visual.offsetWidth;
      // stay in the left column: centring it would push the box over the always-visible title
      S.dx = 0; S.dy = 0; void gw; void vl; void vw;
    } else {
      S.dx = 0; S.dy = 0;
    }
  }

  /* ── WebGL ── */
  let gl = null, prog = null, U = {}, texReady = 0;
  const VS = "attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}";
  const FS = `
precision highp float;
uniform sampler2D t0,t1,t2;
uniform vec2 uRes; uniform float uDpr;
uniform vec4 uRig;   // Cx, Cy, A, blur(display px)
uniform vec4 uRigB;  // bF', bB', sk', Yh'
uniform vec4 uA0,uB0,uA1,uB1,uA2,uB2;  // (cx,cy,a,yh) (bF,bB,sk,-)
uniform vec3 uLid;   // lid: display px per bronpixel (per foto)
uniform vec3 uUp;    // opstaande delen boven de rand
uniform vec4 uW;     // buitendeksel, rok dicht, basis, mix top (basis)
uniform vec2 uW2;    // binnendeksel, mix top (deksel)
uniform vec2 uDepth; // diepte interieur (in A), cos(e) huidig
vec2 uvOf(vec2 s){ return (s - vec2(400.0,300.0)) / 1024.0; }
vec4 tap(sampler2D t, vec2 s, float o){
  if (o < 0.3) return texture2D(t, uvOf(s));
  vec4 c = texture2D(t, uvOf(s)) * 3.0;
  c += (texture2D(t, uvOf(s + vec2(0.0, o*0.5))) + texture2D(t, uvOf(s - vec2(0.0, o*0.5)))) * 2.0;
  c += texture2D(t, uvOf(s + vec2(0.0, o))) + texture2D(t, uvOf(s - vec2(0.0, o)));
  return c / 9.0;
}
float baseY(float y, float w, float u, vec4 A, vec4 B, float up){
  float Cy = uRig.y, Ad = uRig.z;
  float fr = Cy + uRigB.x * w, bk = Cy - uRigB.y * w;
  if (y >= fr){
    float kk = Ad / A.z;
    return A.y + B.x * w + (y - fr) * max(B.z / uRigB.z, 0.625 / kk);
  }
  if (y < bk) return A.y - B.y * w + (y - bk) / up;
  // binnen de rand: verzonken interieur krijgt diepte-parallax
  float Y = y - Cy;
  float v = Y / (Y >= 0.0 ? uRigB.x : uRigB.y);
  float r = sqrt(u*u + v*v);
  float d = uDepth.x * smoothstep(0.94, 0.80, r);
  float Yz = Y - d * Ad * uDepth.y;
  float bs = Yz >= 0.0 ? B.x / uRigB.x : B.y / uRigB.y;
  return A.y + Yz * bs + d * A.z * B.w;
}
void main(){
  vec2 q = vec2(gl_FragCoord.x, uRes.y * uDpr - gl_FragCoord.y) / uDpr;
  float Cx = uRig.x, A = uRig.z, blur = uRig.w;
  float u = (q.x - Cx) / A;
  float w = sqrt(max(0.0, 1.0 - u*u));
  vec4 col = vec4(0.0);

  // ── binnenkant deksel (achter de basis)
  if (uW2.x > 0.001){
    vec4 c = vec4(0.0);
    if (uW2.y < 0.999){
      float k = A / uA1.z; vec2 s = vec2(uA1.x + (q.x - Cx) / k, uA1.w + (q.y - uRigB.w) / uLid.y);
      c += tap(t1, s, blur / uLid.y) * clamp(uA1.w + 0.5 - s.y, 0.0, 1.0) * (1.0 - uW2.y);
    }
    if (uW2.y > 0.001){
      float k = A / uA2.z; vec2 s = vec2(uA2.x + (q.x - Cx) / k, uA2.w + (q.y - uRigB.w) / uLid.z);
      c += tap(t2, s, blur / uLid.z) * clamp(uA2.w + 0.5 - s.y, 0.0, 1.0) * uW2.y;
    }
    col = c * uW2.x;
  }
  // ── basis (interieur + kom)
  if (uW.z > 0.001){
    vec4 c = vec4(0.0);
    if (uW.w < 0.999){
      float k = A / uA1.z; vec2 s = vec2(uA1.x + (q.x - Cx) / k, baseY(q.y, w, u, uA1, uB1, uUp.y));
      c += tap(t1, s, blur / k) * clamp(s.y - uA1.w + 0.5, 0.0, 1.0) * (1.0 - uW.w);
    }
    if (uW.w > 0.001){
      float k = A / uA2.z; vec2 s = vec2(uA2.x + (q.x - Cx) / k, baseY(q.y, w, u, uA2, uB2, uUp.z));
      c += tap(t2, s, blur / k) * clamp(s.y - uA2.w + 0.5, 0.0, 1.0) * uW.w;
    }
    c *= uW.z;
    col = c + col * (1.0 - c.a);
  }
  // ── gesloten foto: onderkant (kom) en buitenkant deksel
  if (uW.y > 0.001 || uW.x > 0.001){
    float k = A / uA0.z; float sx = uA0.x + (q.x - Cx) / k;
    float su = (sx - uA0.x) / uA0.z; float seam = uA0.y + uB0.x * sqrt(max(0.0, 1.0 - su*su));
    if (uW.y > 0.001){
      vec2 s = vec2(sx, baseY(q.y, w, u, uA0, uB0, uUp.x));
      vec4 c = tap(t0, s, blur / k) * clamp((s.y - seam + 0.75) / 1.5, 0.0, 1.0) * uW.y;
      col = c + col * (1.0 - c.a);
    }
    if (uW.x > 0.001){
      vec2 s = vec2(sx, uA0.w + (q.y - uRigB.w) / uLid.x);
      vec4 c = tap(t0, s, blur / uLid.x) * clamp((seam + 0.75 - s.y) / 1.5, 0.0, 1.0) * uW.x;
      col = c + col * (1.0 - c.a);
    }
  }
  gl_FragColor = col;
}`;

  function initGL(){
    try {
      gl = canvas.getContext("webgl", {premultipliedAlpha:true, alpha:true, antialias:false, preserveDrawingBuffer:false})
        || canvas.getContext("experimental-webgl");
    } catch(e){ gl = null; }
    if (!gl) return false;
    const sh = (t, src) => { const s = gl.createShader(t); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s)); return s; };
    try {
      prog = gl.createProgram();
      gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS));
      gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
    } catch(err){ console.warn(err); gl = null; return false; }
    gl.useProgram(prog);
    const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "p"); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    ["t0","t1","t2","uRes","uDpr","uRig","uRigB","uA0","uB0","uA1","uB1","uA2","uB2","uLid","uUp","uW","uW2","uDepth"].forEach(n => U[n] = gl.getUniformLocation(prog, n));
    const setP = (a, b, p) => { gl.uniform4f(U[a], p.cx, p.cy, p.a, p.yh); gl.uniform4f(U[b], p.bF, p.bB, p.sk, Math.cos(p.e)); };
    setP("uA0","uB0",P.closed); setP("uA1","uB1",P.open); setP("uA2","uB2",P.top);
    gl.uniform1i(U.t0, 0); gl.uniform1i(U.t1, 1); gl.uniform1i(U.t2, 2);
    const aniso = gl.getExtension("EXT_texture_filter_anisotropic");
    [IMG.closed, IMG.open, IMG.top].forEach((src, i) => {
      const tex = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0 + i); gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));
      const img = new Image(); img.crossOrigin = "anonymous";
      img.onload = () => {
        const c = document.createElement("canvas"); c.width = c.height = 1024;
        c.getContext("2d").drawImage(img, 0, 0);
        gl.activeTexture(gl.TEXTURE0 + i); gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        if (aniso) gl.texParameterf(gl.TEXTURE_2D, aniso.TEXTURE_MAX_ANISOTROPY_EXT, 4);
        if (++texReady === 3){ root.classList.add("is-ready"); kick(); }
      };
      img.src = src;
    });
    canvas.addEventListener("webglcontextlost", e => { e.preventDefault(); gl = null; noGL(); });
    return true;
  }

  /* ── Eén frame renderen ── */
  let last = performance.now();
  function frame(now){
    const dt = Math.min(0.1, (now - last) / 1000); last = now;
    let busy = false;

    // tween van de voortgang
    if (S.tw){
      const t = clamp((now - S.tw.t0) / S.tw.dur);
      S.p = lerp(S.tw.from, S.tw.to, S.tw.ease(t));
      if (t >= 1) S.tw = null;
      busy = true;
    }
    // subtiel 'gluren' bij hover of als hint
    const peekTarget = (S.hover && S.p < 0.02 && !S.drag) ? 1 : S.peekT;
    const dp = peekTarget - S.peek;
    if (Math.abs(dp) > 0.001){ S.peek += dp * Math.min(1, dt * 7); busy = true; } else S.peek = peekTarget;

    // deksel volgt met een veer → natuurlijke 'snap' en lichte naveer
    const pt = phiTarget(S.p) + S.peek * 7 * D2R * (1 - clamp(S.p * 4));
    if (reduced){ S.phi = pt; S.vphi = 0; }
    else {
      const w = 16, z = 0.42; let rem = dt;
      while (rem > 0){ const h = Math.min(rem, 1/240);
        S.vphi += (w*w*(pt - S.phi) - 2*z*w*S.vphi) * h; S.phi += S.vphi * h; rem -= h; }
      if (Math.abs(pt - S.phi) > 1e-4 || Math.abs(S.vphi) > 1e-3) busy = true;
    }
    const phi = Math.max(0, S.phi);
    const e = eAt(S.p) + S.peek * 1.2 * D2R * (1 - clamp(S.p * 4));
    // camera-afstand: zo dichtbij mogelijk, maar de hele compositie past altijd
    const R1 = rig(phi, e, 1);
    const A = Math.min(S.W * lerp(S.capC, S.capO, zoomAt(S.p)), S.H * S.fitH / (R1.bottom - R1.top + SHADOW_ROOM));
    const R = rig(phi, e, A);

    // compositie verticaal centreren (camera 'kraan')
    const Cx = S.W / 2;
    const centerY = S.H * 0.46;
    const Cy = centerY - (R.top + R.bottom) / 2;
    const Yh = Cy - R.bB;

    // gewichten
    const wTop = sstep(38 * D2R, 53 * D2R, e);
    const lidS = R.lid;
    const aOuter = 1 - sstep(-0.02, 0.07, lidS);
    const aInner = sstep(-0.03, 0.06, lidS);
    const aSkirt = 1 - sstep(4 * D2R, 30 * D2R, phi);
    const aBase = sstep(0, 2.5 * D2R, phi);
    const floor = 0.045;
    const kC = A / P.closed.a, kO = A / P.open.a, kT = A / P.top.a;
    const Lc = kC * Math.max(R.sOut, floor / Math.sin(E0));
    const Lo = kO * Math.max(lidS, floor) / P.open.h;
    const Lt = kT * Math.max(lidS, floor) / P.top.h;
    const upF = Math.cos(e);
    const blurPx = reduced ? 0 : Math.min(2.6, (Math.abs(e - S.lastE) + Math.abs(phi - S.lastPhi) * 0.35) / Math.max(dt, 1/120) * A * 0.012);
    S.lastE = e; S.lastPhi = phi;

    if (gl && texReady === 3){
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(0,0,0,0); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(U.uRes, S.W, S.H); gl.uniform1f(U.uDpr, canvas.width / S.W);
      gl.uniform4f(U.uRig, Cx, Cy, A, blurPx);
      gl.uniform4f(U.uRigB, R.bF, R.bB, R.sk, Yh);
      gl.uniform3f(U.uLid, Lc, Lo, Lt);
      gl.uniform3f(U.uUp, kC * upF / Math.cos(E0), kO * upF / Math.cos(E1), kT * upF / Math.cos(E2));
      gl.uniform4f(U.uW, aOuter, aSkirt, aBase, wTop);
      gl.uniform2f(U.uW2, aInner, wTop);
      gl.uniform2f(U.uDepth, DEPTH, Math.cos(e));
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    // schaduw
    const te = clamp((e - E0) / (E2 - E0));
    const sw = 2 * A * lerp(0.80, 0.98, te);
    const shh = sw * lerp(0.15, 0.34, te);
    const sy = Cy + R.bottom + A * lerp(0.30, 0.02, te);
    shadow.style.width = sw + "px"; shadow.style.height = shh + "px";
    shadow.style.transform = `translate3d(${Cx - sw/2}px, ${sy - shh * lerp(0.2, 0.62, te)}px, 0)`;
    shadow.style.opacity = lerp(1, 0.8, te).toFixed(3);
    // desktop: bijschrift zweeft vlak onder de schaduw mee
    const capY = S.desktop ? Math.min(0, sy + shh * 0.5 + 26 - S.H) : 0;
    caption.style.transform = capY ? `translate3d(0, ${capY.toFixed(1)}px, 0)` : "";

    // layout: visual schuift op zodra de tekst binnenkomt
    const L = liftAt(S.p);
    visual.style.transform = `translate3d(${(S.dx * (1 - L)).toFixed(2)}px, ${(S.dy * (1 - L)).toFixed(2)}px, 0)`;

    // tekst in/uit
    const goingOpen = S.target === 1;
    if (!S.open && goingOpen && S.p > 0.58) setOpen(true);
    if (S.open && (!goingOpen || S.p < 0.45)) setOpen(false);

    if (busy || S.drag) raf = requestAnimationFrame(frame); else raf = 0;
  }
  let raf = 0;
  function kick(){ if (!raf){ last = performance.now(); raf = requestAnimationFrame(frame); } }

  function setOpen(v){
    S.open = v;
    root.classList.toggle("is-open", v);
    if (v) copy.removeAttribute("inert"); else copy.setAttribute("inert", "");
  }

  function go(to, speed = 1){
    S.target = to;
    root.dataset.target = to ? "open" : "closed";
    stage.setAttribute("aria-pressed", to ? "true" : "false");
    stage.setAttribute("aria-label", to ? "Sluit het doosje" : "Open het doosje");
    const dist = Math.abs(to - S.p);
    if (dist < 1e-4){ S.tw = null; kick(); return; }
    const full = reduced ? 450 : (to ? 2500 : 1900);
    S.tw = {from:S.p, to, t0:performance.now(), dur:Math.max(260, full * Math.pow(dist, 0.85) / speed),
            ease: dist > 0.9 ? (t => t) : (t => 1 - Math.pow(1 - t, 2))};
    kick();
  }
  function toggle(){ S.interacted = true; S.peekT = 0; go(S.target ? 0 : 1); }

  /* ── Interactie ── */
  const moveCursor = e => {
    const r = stage.getBoundingClientRect();
    stage.style.setProperty("--cx", (e.clientX - r.left).toFixed(1) + "px");
    stage.style.setProperty("--cy", (e.clientY - r.top).toFixed(1) + "px");
  };
  stage.addEventListener("pointerenter", e => { if (e.pointerType === "mouse"){ moveCursor(e); S.hover = true; stage.classList.add("is-hover"); kick(); } });
  stage.addEventListener("pointerleave", e => { if (e.pointerType === "mouse"){ S.hover = false; stage.classList.remove("is-hover"); kick(); } });
  stage.addEventListener("pointermove", e => { if (e.pointerType === "mouse") moveCursor(e); });
  stage.addEventListener("pointerdown", e => {
    if (e.button !== 0) return;
    S.drag = {x:e.clientX, y:e.clientY, p0:S.p, moved:false, type:e.pointerType, id:e.pointerId, lastY:e.clientY, lastT:performance.now(), v:0};
  });
  window.addEventListener("pointermove", e => {
    const d = S.drag; if (!d || e.pointerId !== d.id) return;
    const dy = e.clientY - d.y;
    if (!d.moved && d.type === "mouse" && Math.abs(dy) > 6){
      d.moved = true; S.tw = null; S.interacted = true; S.peekT = 0;
      try { stage.setPointerCapture(d.id); } catch(_){}
      root.classList.add("is-dragging");
    }
    if (d.moved){
      // omhoog slepen = camera omhoog + deksel open
      S.p = clamp(d.p0 - dy / (S.H * 0.7));
      const now = performance.now(); d.v = (d.lastY - e.clientY) / Math.max(1, now - d.lastT); d.lastY = e.clientY; d.lastT = now;
      S.target = S.p > 0.5 ? 1 : 0;
      root.dataset.target = S.target ? "open" : "closed";
      kick();
    }
  });
  const endDrag = e => {
    const d = S.drag; if (!d || (e && e.pointerId !== d.id)) return;
    S.drag = null; root.classList.remove("is-dragging");
    if (d.moved){
      if (performance.now() - d.lastT > 120) d.v = 0;   // stilgehouden → geen 'flick'
      const to = Math.abs(d.v) > 0.35 ? (d.v > 0 ? 1 : 0) : (S.p > 0.5 ? 1 : 0);
      go(to, 1.4);
    } else if (e && e.type === "pointerup"){ toggle(); }
    kick();
  };
  window.addEventListener("pointerup", endDrag);
  window.addEventListener("pointercancel", e => { if (S.drag){ S.drag.moved = true; } endDrag(e); });
  stage.addEventListener("keydown", e => { if (e.key === "Enter" || e.key === " "){ e.preventDefault(); toggle(); } });

  // hint: deksel 'ademt' even open zolang er nog niet getikt is
  if (!reduced){
    let n = 0;
    const hint = () => {
      if (S.interacted || n > 6) return;
      if (S.target === 0 && !S.hover && visible && !document.hidden){
        n++; S.peekT = 1; kick();
        setTimeout(() => { S.peekT = 0; kick(); }, 650);
      }
      setTimeout(hint, 4200);
    };
    setTimeout(hint, 1800);
  }

  // hints alleen als de sectie in beeld is
  let visible = true;
  if ("IntersectionObserver" in window){
    new IntersectionObserver(es => { visible = es[0].isIntersecting; if (visible) kick(); }).observe(root);
  }

  const onResize = () => { measure(); kick(); };
  if ("ResizeObserver" in window) new ResizeObserver(onResize).observe(stage); else addEventListener("resize", onResize);

  const noGL = () => {
    root.classList.add("no-gl");
    root.querySelectorAll(".er-fallback img").forEach(im => { if (!im.src) im.src = IMG[im.dataset.k]; });
  };
  if (!initGL()) noGL();
  measure();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { measure(); kick(); });
  kick();

  // klein API'tje, bv. om vanuit een andere knop te openen: EufraReveal.open()
  window.EufraReveal = {
    open:   () => { S.interacted = true; go(1); },
    close:  () => { S.interacted = true; go(0); },
    toggle,
    seek(p){ S.interacted = true; S.tw = null; S.p = clamp(p); S.phi = phiTarget(S.p); S.vphi = 0;
             S.target = S.p > .5 ? 1 : 0; root.dataset.target = S.target ? "open" : "closed"; kick(); },
    state:  S
  };
    document.addEventListener("eu:set-change", (e) => root.classList.toggle("is-pack", !!(e.detail && e.detail.pack)));
  };
  const start = () => document.querySelectorAll(".er[data-img-closed]").forEach((el) => { if (!el.dataset.erReady) { el.dataset.erReady = "1"; boot(el); } });
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start); else start();
  document.addEventListener("shopify:section:load", start);
})();
